import jwtDecode from 'jwt-decode';
import { ApiStatus } from 'Enums';
import { AUTH_SESSION } from 'Reducers/auth/types';
import { CONFIG_UPDATE } from 'Reducers/config/types';
import { SET_ENV } from 'Reducers/env/types';
import { INITIALIZATION_ERROR } from 'Reducers/nav/types';
import { generateLibreUserAgentHeader, setLsapiCountryRedirectUrl, setUrls } from 'Utilities/dataHelpers';
import mediator from 'Utilities/mediator';
import { createAuthSession, getAuthSession, persistAuthSession } from 'Utilities/session';
import { getSessionObject } from 'Utilities/storage';
import { store } from 'Utilities/store';
import { updateConfig, updateLibreConfig } from 'Services/config';
import { waitForToken } from './appBridge';
import { getPatientService } from './libre_api';
const handleInitializationError = error => {
  store?.dispatch({
    type: INITIALIZATION_ERROR,
    initErrorStatus: error.status ?? 404
  });
};

/**
 * Handles region mismatch errors to update the API base URL and retry the initialization
 * @param error - Axios error containing the 'x-api-url' header response (redirect url)
 * @param handleSubmit - Callback function to retry the call
 */
const handleWrongRegionError = async (error, handleSubmit) => {
  const redirectUrl = error.response?.headers?.['x-api-url'];
  if (!redirectUrl) {
    handleInitializationError(error);
    return;
  }
  setLsapiCountryRedirectUrl(redirectUrl);
  await handleSubmit();
};
const root = async pageCtx => {
  try {
    await setupInitialization(pageCtx.query);
  } catch (err) {
    const error = err;
    if (error.response?.status === 400) {
      const code = error.response.data?.code;
      if (code === ApiStatus.WRONG_REGION) {
        await handleWrongRegionError(error, () => root(pageCtx));
        return;
      }
    }
    handleInitializationError(error);
  }
};
const setupInitialization = async (queryParams, forceCountry) => {
  const envSession = getSessionObject('env');
  const configSession = getSessionObject('config');
  const country = forceCountry ?? queryParams?.country;
  if (!queryParams && envSession && configSession) {
    store.dispatch({
      type: CONFIG_UPDATE,
      config: configSession
    });
    store.dispatch({
      type: SET_ENV,
      env: envSession
    });
    setUrls(configSession, country);
    return;
  }
  const {
    app,
    pId
  } = queryParams || {};
  await updateConfig(country, app, pId);
};
const setupLoginLogic = pageCtx => {
  let url = '/login';
  if (pageCtx.querystring) {
    url = url.concat(`?${pageCtx.querystring}`);
  }
  pageCtx.page.redirect(url);
  setTimeout(() => mediator.publish('router:reload'));
};

/**
 * TEMPORARY: This function will be removed once the integration with the new APIs is completed.
 * It was created to allow the test team to test the Connected Apps Libre features before the API integration is finished.
 * URL path: /connections?deviceId={deviceId}&appVersion={appVersion}&osType={osType}&osVersion={osVersion}&app={app}&country={country}&token={token}
 *
 * @param token
 * @param queryParams
 * @returns
 */
const setupLibreLogic = async queryParams => {
  const token = await waitForToken(queryParams?.token);
  const {
    app,
    country,
    osType,
    osVersion,
    appVersion
  } = queryParams || {};
  await updateLibreConfig(country, app, appVersion, osType, osVersion);
  const decodedToken = jwtDecode(token);
  const xUserAgent = generateLibreUserAgentHeader(app, appVersion, osType, osVersion);
  const patient = await getPatientService({
    patientId: decodedToken.id,
    authToken: token,
    xUserAgent
  });
  if (!patient) {
    throw {
      status: 404,
      message: 'Patient not found'
    };
  }
  const authSession = createAuthSession(token, patient);
  store.dispatch({
    type: AUTH_SESSION,
    authSession
  });
  persistAuthSession(authSession);
};
const protectedPageAction = async pageCtx => {
  /**
   * TEMPORARY: This validation will be removed once the integration with the new APIs is completed.
   * It was created to allow the test team to test the Connected Apps Libre features before the API integration is finished.
   * URL path: /connections?deviceId={deviceId}&appVersion={appVersion}&osType={osType}&osVersion={osVersion}&app={app}&country={country}&token={token}
   */
  if (pageCtx.pathname === '/connections' && pageCtx.query?.app === 'libre1') {
    try {
      await setupLibreLogic(pageCtx.query);
    } catch (err) {
      handleInitializationError(err);
    }
    return;
  }
  const authSession = getAuthSession();
  if (!authSession) {
    setupLoginLogic(pageCtx);
    return;
  }
  store.dispatch({
    type: AUTH_SESSION,
    authSession
  });
  try {
    await setupInitialization(pageCtx.query, authSession.user.country);
  } catch (err) {
    const error = err;
    if (error?.response?.status === 400) {
      await handleWrongRegionError(error, () => protectedPageAction(pageCtx));
      return;
    }
    handleInitializationError(error);
  }
};
const publicPageAction = () => Promise.resolve();

// Create your paths that you want to handle here
// you want to most specific earlier and the least specific later
// Note: if one action throws an error (calls promise reject) then the handlePaths
// will move onto the next matching route
const routeActions = [{
  regex: [/^\/connections/i, /^\/llu-pending-connection/i, /^\/llu-connection/i, /^\/llu-add-connection/i, /^\/lv-practice-connection/i, /^\/lv-practice-invitation/i, /^\/lds-connection/i, /^\/lv-add-practice/i, /^\/partner-add-connection/i, /^\/partner-connection/i, /^\/partner-confirm-connection/i],
  action: protectedPageAction
}, {
  regex: [/^\/pump-state/i],
  action: publicPageAction
}, {
  regex: [/^\.*/],
  action: root
}];
export const handlePaths = ctx => {
  const doActions = [];
  for (const check of routeActions) {
    for (const r of check.regex) {
      if (ctx.canonicalPath.match(r)) {
        // Add valid actions to chain later
        doActions.push(check.action);
      }
    }
  }

  // Start with a rejected promise
  let p = Promise.reject();

  // Build a negative promise chain that will try the next action if the previous one failed
  // Once an action succeeds the chain ends and the app starts
  for (let i = 0; i < doActions.length; i++) {
    p = p.catch(() => doActions[i](ctx));
  }
  return p;
};